/**
 * Comptabilité : lecture du rapport d'encaissements renvoyé par l'API.
 *
 * La page ne lit jamais la réponse brute. Tout passe par
 * `normalizeAccountingReport`, qui garantit des nombres là où l'on attend des
 * montants et une liste là où l'on attend l'historique : l'API est neuve, et
 * un champ manquant ne doit pas faire tomber l'écran.
 *
 * Forme attendue (fournie par le backend) :
 *   {
 *     filters_applied: { classroom_id, start_date, end_date, month },
 *     summary: { total_expected, total_collected, total_pending },
 *     transactions_count,
 *     transactions_history: [
 *       { payment_id, registration_number, label, payment_type, status,
 *         amount, is_split, provider, tx_id, date }
 *     ]
 *   }
 */

/** Teinte d'un statut de paiement, pour la pastille de l'historique. */
export const STATUS_TONE = {
  SETTLED: "settled",
  PENDING: "pending",
  OVERDUE: "overdue",
  OTHER: "other",
};

/** Moyen de paiement saisi à la main, au guichet de l'école. */
export const MANUAL_PROVIDER = "manual";

/** Valeurs que l'API renvoie à la place d'une référence absente. */
const EMPTY_REFERENCES = ["", "n/a", "na", "none", "null", "-"];

function toNumber(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function toText(value) {
  return value === null || value === undefined ? "" : String(value).trim();
}

/**
 * Date d'une transaction.
 *
 * L'API renvoie « 2026-10-05 11:17 » : sans le « T », certains navigateurs
 * refusent de lire la chaîne. On le rétablit avant de la confier à `Date`.
 * Toute valeur illisible donne `null`, que la page affiche comme un tiret.
 */
export function parseTransactionDate(value) {
  const text = toText(value);
  if (!/^\d{4}-\d{2}-\d{2}/.test(text)) return null;
  const date = new Date(text.replace(" ", "T"));
  return Number.isNaN(date.getTime()) ? null : date;
}

/** Référence d'opérateur, ou chaîne vide quand l'API n'en a pas. */
export function referenceOf(value) {
  const text = toText(value);
  return EMPTY_REFERENCES.includes(text.toLowerCase()) ? "" : text;
}

/**
 * Clé du moyen de paiement.
 *
 * Les opérateurs arrivent sous leur code (`mtn-momo`, `orange-money`…) ; un
 * encaissement au guichet arrive en clair (« Direct / Manual »). Les deux
 * sont ramenés à une clé que la page sait illustrer. Un code inconnu est
 * rendu tel quel, en minuscules : mieux vaut l'afficher que le perdre.
 */
export function providerKeyOf(provider) {
  const text = toText(provider).toLowerCase();
  if (!text) return "";
  if (text.includes("manual") || text.includes("direct") || text.includes("cash")) {
    return MANUAL_PROVIDER;
  }
  if (text === "eumm") return "express-union";
  return text;
}

/**
 * Intitulé lisible d'un moyen de paiement que la page ne connaît pas.
 *
 * Le code brut (« orange_money », « operator-code ») devient « Orange Money »,
 * « Operator Code » : séparateurs remplacés par des espaces, première lettre
 * de chaque mot en majuscule. Le reste du mot est gardé tel quel, pour ne pas
 * abîmer un sigle déjà écrit en capitales.
 */
export function providerLabelOf(provider) {
  return toText(provider)
    .replace(/[-_]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

/** Teinte d'un statut, quelle que soit sa casse. */
export function statusToneOf(status) {
  switch (toText(status).toLowerCase()) {
    case "settled":
    case "paid":
    case "success":
      return STATUS_TONE.SETTLED;
    case "pending":
    case "accepted":
      return STATUS_TONE.PENDING;
    case "overdue":
    case "failed":
      return STATUS_TONE.OVERDUE;
    default:
      return STATUS_TONE.OTHER;
  }
}

/** Une scolarité, ou un autre paiement (frais d'examen, tenue…). */
export function isTuition(paymentType) {
  return toText(paymentType).toLowerCase() === "schoolfees";
}

/**
 * Taux de recouvrement, en pourcentage entier, borné entre 0 et 100.
 * Rien d'attendu : rien à recouvrer, le taux vaut 0 et non une division par
 * zéro.
 */
export function collectionRate(expected, collected) {
  const due = toNumber(expected);
  if (due <= 0) return 0;
  const rate = Math.round((toNumber(collected) / due) * 100);
  return Math.min(100, Math.max(0, rate));
}

/**
 * Clé de rendu d'une transaction.
 *
 * L'identifiant du paiement ne suffit pas : un paiement fractionné renvoie
 * une ligne par versement, toutes sous le même `payment_id` — et dans
 * l'exemple du backend, trois lignes sont identiques en tout point. La
 * position dans la liste les départage.
 */
export function transactionKey(transaction, index) {
  return `${transaction.paymentId || "tx"}-${transaction.reference || ""}-${index}`;
}

function normalizeTransaction(raw) {
  const item = raw || {};
  return {
    paymentId: toText(item.payment_id),
    registrationNumber: toText(item.registration_number),
    label: toText(item.label),
    paymentType: toText(item.payment_type),
    isTuition: isTuition(item.payment_type),
    status: toText(item.status),
    tone: statusToneOf(item.status),
    amount: toNumber(item.amount),
    isSplit: item.is_split === true,
    provider: toText(item.provider),
    providerKey: providerKeyOf(item.provider),
    reference: referenceOf(item.tx_id),
    date: parseTransactionDate(item.date),
  };
}

/**
 * Plus récentes d'abord. Les transactions sans date lisible passent en fin
 * de liste, dans l'ordre où l'API les a renvoyées.
 */
export function sortTransactions(transactions) {
  return transactions
    .map((transaction, position) => ({ transaction, position }))
    .sort((a, b) => {
      const left = a.transaction.date;
      const right = b.transaction.date;
      if (left && right) return right - left || a.position - b.position;
      if (left) return -1;
      if (right) return 1;
      return a.position - b.position;
    })
    .map(({ transaction }) => transaction);
}

/** Réponse de l'API, mise en forme pour la page. */
export function normalizeAccountingReport(payload) {
  const data = payload || {};
  const summary = data.summary || {};
  const filters = data.filters_applied || {};
  const history = Array.isArray(data.transactions_history)
    ? data.transactions_history
    : [];

  const expected = toNumber(summary.total_expected);
  const collected = toNumber(summary.total_collected);
  const transactions = sortTransactions(history.map(normalizeTransaction));

  return {
    filters: {
      classroomId: filters.classroom_id || null,
      startDate: filters.start_date || null,
      endDate: filters.end_date || null,
      month: filters.month || null,
    },
    summary: {
      expected,
      collected,
      pending: toNumber(summary.total_pending),
      rate: collectionRate(expected, collected),
    },
    count: Number.isFinite(Number(data.transactions_count))
      ? Number(data.transactions_count)
      : transactions.length,
    transactions,
  };
}

/**
 * Filtre local de l'historique : recherche libre et statut.
 *
 * La recherche porte sur ce qu'on a sous les yeux en cherchant un versement
 * précis — matricule, libellé, référence de l'opérateur.
 */
export function filterTransactions(transactions, { search = "", tone = "" } = {}) {
  const needle = toText(search).toLowerCase();
  return transactions.filter((transaction) => {
    if (tone && transaction.tone !== tone) return false;
    if (!needle) return true;
    return [transaction.registrationNumber, transaction.label, transaction.reference]
      .some((field) => field.toLowerCase().includes(needle));
  });
}

/** Mois au format de l'API : « 2026-10 ». */
export function monthKeyOf(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  return `${year}-${month}`;
}

/** Jour au format de l'API : « 2026-10-05 ». */
export function dayKeyOf(date) {
  const day = String(date.getDate()).padStart(2, "0");
  return `${monthKeyOf(date)}-${day}`;
}

/** Mois proposés en mode mois : de janvier au mois courant, année en cours. */
export function monthsOfYear(today = new Date()) {
  const months = [];
  for (let month = 0; month <= today.getMonth(); month += 1) {
    months.push(monthKeyOf(new Date(today.getFullYear(), month, 1)));
  }
  return months;
}

/** Nombre d'années consultables avant l'année en cours, en mode période. */
export const RANGE_YEARS_BACK = 2;

/**
 * Bornes du mode période : du 1er janvier d'il y a deux ans à aujourd'hui.
 * En 2026, de 2024-01-01 à la date du jour ; au-delà, aucun encaissement ne
 * peut encore exister.
 */
export function rangeBounds(today = new Date()) {
  return {
    min: `${today.getFullYear() - RANGE_YEARS_BACK}-01-01`,
    max: dayKeyOf(today),
  };
}

/** Deux façons de choisir la période : un mois, ou une date de début et de fin. */
export const PERIOD_MODE = {
  MONTH: "month",
  RANGE: "range",
};

/**
 * Une plage de dates est-elle exploitable ?
 *
 * Les deux bornes sont exigées, et la fin ne peut pas précéder le début :
 * l'API répondrait par une période vide, que l'on confondrait avec une
 * absence d'encaissements. Avec `bounds`, la plage doit aussi y tenir — le
 * calendrier les impose, mais une date tapée au clavier peut les ignorer.
 * Les dates « AAAA-MM-JJ » se comparent comme des chaînes.
 */
export function isValidRange(startDate, endDate, bounds = null) {
  if (!startDate || !endDate || startDate > endDate) return false;
  if (bounds && (startDate < bounds.min || endDate > bounds.max)) return false;
  return true;
}

/**
 * Paramètres de la requête, au format fourni par le backend :
 *   { month: "YYYY-MM" }  ou  { start_date: "YYYY-MM-DD", end_date: "YYYY-MM-DD" }
 *   plus, si une classe est choisie, { classroom_id }
 *
 * Jamais le mois et les dates à la fois : la période vient de l'un ou des
 * autres, et envoyer les deux laisserait le backend arbitrer à notre place.
 * Un mois est envoyé tel quel ; le backend en déduit lui-même les bornes.
 * Sans classe choisie, `classroom_id` est omis : toutes les classes.
 */
export function reportParams({ mode, month, startDate, endDate, classroomId } = {}) {
  let params = {};
  if (mode === PERIOD_MODE.RANGE) {
    if (isValidRange(startDate, endDate)) {
      params = { start_date: startDate, end_date: endDate };
    }
  } else if (month) {
    params = { month };
  }
  if (classroomId) params.classroom_id = classroomId;
  return params;
}

/** Spécialité « vide » renvoyée par l'API, à ne pas afficher. */
const NO_SPECIALITY = "Pas de spécialité";

/**
 * Classes proposées dans le filtre, à partir de `/api/grades`.
 *
 * L'API renvoie les niveaux, chacun avec ses classes : on les met à plat,
 * dans l'ordre des niveaux. L'intitulé suit celui des cartes de classe —
 * niveau, spécialité s'il y en a une, numéro.
 */
export function classroomOptions(grades) {
  if (!Array.isArray(grades)) return [];
  const options = [];
  grades.forEach((grade) => {
    const classrooms = Array.isArray((grade || {}).classrooms) ? grade.classrooms : [];
    classrooms.forEach((classroom) => {
      if (!classroom || !classroom.id) return;
      const speciality =
        classroom.speciality_code && classroom.speciality_code !== NO_SPECIALITY
          ? classroom.speciality_code
          : "";
      options.push({
        id: classroom.id,
        label: [grade.name, speciality, classroom.number]
          .map(toText)
          .filter(Boolean)
          .join(" "),
      });
    });
  });
  return options;
}
